/* eslint-disable eqeqeq */
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import '../../styles/Login.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSignIn, useIsAuthenticated } from 'react-auth-kit';
import { clearLoginError, login } from '../../redux/actions/user.action';
import { logoutUser } from '../../redux/actions/user.action';
import { useSignOut } from 'react-auth-kit';
import socket from '../../config/socket';
import useTranslations from '../../customHook/useTranslations';
import translations from '../../content/PagesContent/loginPage/loginPageTranslations.json';
import Modal from '../Modal/Modal';
import ModalForgotPassword from './ModalForgotPassword';

function Login() {
  // Получение функции dispatch для отправки действий в Redux store.
  const dispatch = useDispatch();

  // получение словаря для статических данных в зависимости от выбранного языка
  const t = useTranslations(translations);
  const signOut = useSignOut();

  // Получаем данные о пользователе из Redux store.
  const userData = useSelector((state) => state.user);

  // Локальное состояние для учетных данных пользователя.
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  // локальное состояние для почты
  const [email, setEmail] = useState('');

  // состояние для открытия модалки
  const [openModal, setOpenModal] = useState(false);

  // флаги для отображения контента модалки
  const [showResetForm, setShowResetForm] = useState(false);
  const [showResetSuccess, setShowResetSuccess] = useState(false);

  // Локальное состояние для отслеживания ошибок входа в систему.
  const [error, setError] = useState('');

  // Hook для программной навигации.
  const navigate = useNavigate();

  // Получаем путь
  const location = useLocation();

  // если сброс пароля открываем модалку с информацией
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const shouldOpenModal = searchParams.get('isResetPassword') === 'true';

    if (shouldOpenModal) {
      setOpenModal(true);
      setShowResetSuccess(true);
    }
  }, [location.search]);

  // Hooks от react-auth-kit для управления аутентификацией.
  const signIn = useSignIn();
  const isAuthenticated = useIsAuthenticated();

  // Обработчик события для входа в систему.
  const handleLogin = (event) => {
    event.preventDefault();
    // Отправка действия входа в Redux.
    dispatch(clearLoginError());
    dispatch(login({ username: username, password: password }));
  };

  // Побочный эффект для обработки успешного входа в систему.
  useEffect(() => {
    if (userData.user && !isAuthenticated()) {
      // Вход в систему с использованием react-auth-kit.
      signIn({
        token: userData?.accessToken, // Установка токена (пример).
        refreshToken: userData?.refreshToken,
        expiresIn: 10000,
        tokenType: 'Bearer',
        authState: userData, // Установка состояния аутентификации.
      });
      //подключение сокета после успешного входа
      socket.auth = { token: userData?.accessToken, refreshToken: userData?.refreshToken }; // Установка токена для сокета
      socket.connect(); // Подключаем сокет
      navigate('/'); // Перенаправление на главную страницу, дальше роутер разберется.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData.user, username, navigate, signIn, isAuthenticated]);

  // Побочный эффект для обработки ошибки входа.
  useEffect(() => {
    if (userData.error) {
      setError(userData.error);
      setUsername('');
      setPassword('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData.error]);

  useEffect(() => {
    dispatch(logoutUser({ logout: true, error: userData.error }));
    signOut();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  function handleEnter() {
    if (username && password) return handleLogin();

    // После ввода логина Enter переведет фокус на пароль
    if (document.activeElement.id == 'username') document.querySelector('#password').focus();
  }

  // Рендеринг компонента входа в систему.
  return (
    <>
      {!openModal && (
        <div
          className="d-flex flex-column flex-nowrap my-auto mx-auto"
          onKeyDown={(e) => e.key == 'Enter' && handleEnter()}
        >
          <div
            className="card-container d-flex flex-column flex-nowrap align-items-center"
            style={{ minHeight: '580px' }}
          >
            <h3 className="login-text">
              {!showResetForm ? t?.authTitle || 'Авторизация' : t?.resetPasswordTitle || 'Сброс пароля'}
            </h3>
            <div className="login-line"></div>
            <span className={` w-75 login-error-text ${error ? 'visible' : 'hidden'}`}>
              <img className="login_cell_id_img" src="/image/error.svg" alt="Иконка" />
              {error ? error : t?.errorMessage || 'Неверный логин или пароль'}
            </span>
            {!showResetForm && !openModal && (
              <form className="h-100 w-75 mb-auto flex-grow-1">
                <label className={username ? 'visible' : 'hidden'} htmlFor="username">
                  {t?.usernameLabel || 'Логин'}
                </label>
                <input
                  id="username"
                  type="text"
                  className={`login-input w-100 ${error ? 'login-input-error' : ''}`}
                  name="username"
                  placeholder={t?.usernameLabel || 'Логин'}
                  value={username || ''}
                  onChange={(event) => setUsername(event.target.value)}
                  onFocus={() => error && setError('')}
                  tabIndex={1}
                  required
                  autoFocus
                  autoComplete="username"
                />

                <label htmlFor="password" className={password ? 'visible' : 'hidden'}>
                  {t?.passwordLabel || 'Пароль'}
                </label>
                <input
                  id="password"
                  type="password"
                  className={`login-input w-100 ${error ? 'login-input-error' : ''}`}
                  name="password"
                  placeholder={t?.passwordLabel || 'Пароль'}
                  value={password || ''}
                  tabIndex={2}
                  onFocus={() => error && setError('')}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                  autoComplete="current-password"
                />

                <button
                  className={`add-button w-100 ${username && password ? 'btn-success' : ''}`}
                  onClick={handleLogin}
                  tabIndex={3}
                  disabled={!(username && password)}
                >
                  {t?.loginButton || 'Войти'}
                </button>
                <button
                  className="forgot-button"
                  onClick={() => {
                    dispatch(clearLoginError());
                    setShowResetForm(true);
                    setError(false);
                  }}
                  tabIndex={3}
                >
                  {t?.forgotPassword || 'Забыли пароль?'}
                </button>
              </form>
            )}
            {showResetForm && !openModal && (
              <ModalForgotPassword
                setShowResetForm={setShowResetForm}
                setError={setError}
                error={error}
                t={t}
                email={email}
                setEmail={setEmail}
              />
            )}
          </div>
          <div className="d-flex flex-nowrap flex-column align-items-center">
            <img className="login-info_svg" src="/image/Infosignal.svg" alt="" />
            <span className="login-info_text">
              {t?.systemDescription || 'Система для учета устройств, управления бригадами, контроля и отчетности'}
              {/* Система для учета устройств, управления <br /> бригадами, контроля и отчетности */}
            </span>
          </div>
        </div>
      )}
      <Modal
        isOpen={openModal}
        onClose={() => {
          setOpenModal(false);
          setShowResetForm(false);
          setShowResetSuccess(false);
          dispatch(clearLoginError());
        }}
        width="50%"
        height="70%"
      >
        {showResetSuccess && (
          <div className="success-block">
            <h3 className="title-forgot">{t?.resetPasswordTitle || 'Сброс пароля'}</h3>
            <h5 className="success-info">
              {t?.successMessage || 'Сброс пароля прошел успешно, новый пароль отправлен на указанную почту.'}
            </h5>
            <div className="d-flex flex-nowrap flex-column align-items-center">
              <img className="login-info_svg" src="/image/Infosignal.svg" alt="" />
              <span className="login-info_text">
                {t?.systemDescription || 'Система для учета устройств, управления бригадами, контроля и отчетности'}
                {/* Система для учета устройств, управления <br /> бригадами, контроля и отчетности */}
              </span>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
}

export default Login;
